import * as React from 'react'
import useRouter from 'use-react-router'

import { Section } from '@espoon-voltti/react-components/layout'
import {
  Button,
  Column,
  Columns
} from '@espoon-voltti/react-components/elements'
import { LabelValueItem } from '@espoon-voltti/react-components/components'

import { LabelValueList } from '@espoon-voltti/react-voltti/components/label-value-list'
import { closeWithSideEffect } from '@espoon-voltti/react-voltti/components/modal'

import { Org } from '@espoon-voltti/nuora-domain/applicants'

import { GlobalModal } from '@espoon-voltti/nuora-app-common/globals'

import { t } from '../../i18n'
import { getEditOrgLink } from '../../utils/service-url-providers'
import { Store } from '../../services/use-store'
import { handleError } from '../../error-handling'
import { localizedPathnames, orgIdHash } from '../../utils/localized-pathname'

import { AppList } from './app-list'
import { CreateAppBtn } from './create-app-btn'
import { CustomAreaCard } from './custom-area-card'
import { GrantPeriodType } from '@espoon-voltti/nuora-domain/grants'
import { setModalFocus } from '../../utils/modal-focus'

interface OrgListProps {
  orgs: Org[]
}
export const OrgList: React.FC<OrgListProps> = (p) => {
  return (
    <>
      {p.orgs.map((org) => (
        <OrgListItem key={org.id} org={org} />
      ))}
    </>
  )
}

interface OrgListItemProps {
  org: Org
}
const OrgListItem: React.FC<OrgListItemProps> = (p) => {
  const router = useRouter()
  const scrollToEl = React.useRef<HTMLDivElement>(null)

  const store = Store.use()
  const orgDetails = createOrgDetails(p.org)
  const orgApps = store.appsByOrg(p.org.id)

  const incompleteAppsCount = orgApps.filter(
    (x) => x.state === 'DRAFT' || x.state === 'SIGNING'
  ).length

  const [open, setOpen] = React.useState(orgApps.length > 0)
  const activePeriods = store.activePeriods()

  React.useLayoutEffect(() => {
    if (router.location.hash === orgIdHash(p.org.id) && scrollToEl.current) {
      const el = scrollToEl.current
      const getTop = () => el.getBoundingClientRect().top

      const top = getTop()
      // negative value means that element is scrolled out from the top of the viewport
      if (top < 0) {
        // let's first scroll to page beginning and then recalculate top
        window.scrollTo({ top: 0 })
        window.scrollTo({ top: getTop() })
      } else {
        window.scrollTo({ top })
      }
    }
  }, [router.location])

  const handleToggle = () => setOpen(!open)
  const handleCreate =
    (grantPeriodId: string, grantPeriodType: GrantPeriodType) => async () => {
      try {
        const draft = await store.createDraft(
          p.org.id,
          grantPeriodId,
          grantPeriodType
        )
        const url = localizedPathnames.application(draft.id)
        router.history.push(url)
      } catch (e) {
        handleError(e)
      }
    }
  const handleDelete = (appId: string) => {
    GlobalModal.get().showCancellable(
      {
        title: t(['common', 'modal', 'deleteApp', 'title']),
        text: t(['common', 'modal', 'deleteApp', 'text']),
        confirmButtonLabel: t([
          'common',
          'modal',
          'deleteApp',
          'confirmButtonLabel'
        ])
      },
      closeWithSideEffect(() => store.deleteApp(appId).catch(handleError))
    )
    setModalFocus()
  }

  return (
    <div ref={scrollToEl}>
      <CustomAreaCard
        dataQa={`org-${p.org.id}`}
        title={p.org.name}
        subtitle={t(['views', 'home', 'org', 'incompleteApps'], {
          count: incompleteAppsCount
        })}
        open={open}
        header={activePeriods.map((x) => (
          <CreateAppBtn
            key={x.id}
            grantPeriodId={x.id}
            onClick={handleCreate(x.id, x.type)}
            label={t(['views', 'home', 'org', 'applyForGrant', x.type])}
            text={t(['views', 'home', 'org', 'applyForGrant', 'date'], {
              date: x.endDatetime.format('fi')
            })}
          />
        ))}
        onToggle={handleToggle}
      >
        <Section>
          <Columns>
            <Column>
              <LabelValueList dataQa="orgInfo" values={orgDetails} />
            </Column>
            <Column size={'narrow'}>
              <a href={getEditOrgLink(p.org.id)} tabIndex={-1}>
                <Button dataQa={'editOrg'}>
                  {t(['views', 'home', 'org', 'showDetails'])}
                </Button>
              </a>
            </Column>
          </Columns>
        </Section>

        <Section dataQa="appList">
          {orgApps.length > 0 ? (
            <AppList
              apps={orgApps}
              grantPeriods={store.grantPeriods}
              onDelete={handleDelete}
            />
          ) : null}
        </Section>
      </CustomAreaCard>
    </div>
  )
}

const createOrgDetails = (org: Org): LabelValueItem[] => [
  {
    dataQa: 'postalAddress',
    label: t(['org', 'address', 'postalAddress', 'label']),
    value: org.address.postalAddress
  },
  {
    dataQa: 'postalCode',
    label: t(['org', 'address', 'postalCode', 'label']),
    value: org.address.postalCode
  },
  {
    dataQa: 'postOffice',
    label: t(['org', 'address', 'postOffice', 'label']),
    value: org.address.postOffice
  },
  {
    dataQa: 'email',
    label: t(['org', 'email', 'label']),
    value: org.email
  },
  {
    dataQa: 'bankAccount',
    label: t(['org', 'bankAccount', 'label']),
    value: org.bankAccount
  },
  ...org.representatives.map((r) => {
    return {
      dataQa: 'representative',
      value: `${r.firstName} ${r.lastName}, ${r.email}`,
      label: t(['org', 'representativeRole', r.role])
    }
  })
]
